import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(relativeTime);

// Standardize on America/New_York (EST/EDT)
const DEFAULT_TIMEZONE = 'America/New_York';

/**
 * Formats a date string or number to a consistent EST/EDT format
 */
export const formatDateTime = (date?: string | number | Date, format = 'YYYY-MM-DD HH:mm:ss') => {
  if (!date) return '-';
  return dayjs.utc(date).tz(DEFAULT_TIMEZONE).format(format);
};

/**
 * Formats a duration in seconds to a readable string (e.g. 05:23)
 */
export const formatDuration = (seconds: number) => {
  if (!seconds || seconds < 0) return '00:00';
  const hrs = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  const secs = Math.floor(seconds % 60);
  
  if (hrs > 0) {
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  }
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Gets a dayjs object in the default timezone
 */
export const getESTDate = (date?: string | number | Date) => {
  return dayjs.utc(date).tz(DEFAULT_TIMEZONE);
};

const normalizeRangeValue = (value: unknown): dayjs.Dayjs | null => {
  if (value == null || value === '') return null;
  if (dayjs.isDayjs(value)) return value;
  const parsed = dayjs(value as string | number | Date);
  return parsed.isValid() ? parsed : null;
};

/**
 * Convert a date range to API start_dt/end_dt using full EST calendar days (inclusive).
 * Works with dayjs objects, ISO strings, or ProTable "YYYY-MM-DD HH:mm:ss" strings.
 */
export const dateRangeToApiFilter = (
  range?: unknown,
): { start_dt?: string; end_dt?: string } => {
  if (!Array.isArray(range) || range.length < 2) return {};
  const start = normalizeRangeValue(range[0]);
  const end = normalizeRangeValue(range[1]);
  if (!start || !end) return {};
  const startDate = start.tz(DEFAULT_TIMEZONE, true).format('YYYY-MM-DD');
  const endDate = end.tz(DEFAULT_TIMEZONE, true).format('YYYY-MM-DD');
  return {
    start_dt: dayjs.tz(startDate, DEFAULT_TIMEZONE).startOf('day').toISOString(),
    end_dt: dayjs.tz(endDate, DEFAULT_TIMEZONE).endOf('day').toISOString(),
  };
};

/** @deprecated Use dateRangeToApiFilter — same EST inclusive day bounds. */
export const dateTimeRangeToApiFilter = dateRangeToApiFilter;

/**
 * Resolve date filters from ProTable search params (handles transform output or raw timestamp field).
 */
export const pickDateFilterParams = (
  params?: Record<string, unknown>,
): { start_dt?: string; end_dt?: string } => {
  if (!params) return {};
  if (params.start_dt && params.end_dt) {
    return {
      start_dt: String(params.start_dt),
      end_dt: String(params.end_dt),
    };
  }
  if (params.timestamp) {
    return dateRangeToApiFilter(params.timestamp);
  }
  return {};
};

/**
 * Calculates duration from a start time to now in seconds
 */
export const calculateDurationFromStart = (startTime?: string | number | Date) => {
  if (!startTime) return 0;
  const start = dayjs.utc(startTime);
  const now = dayjs.utc();
  return now.diff(start, 'second');
};

export default dayjs;
